'use client';

import React, { useState } from 'react';
import { X, FolderPlus, Image as ImageIcon } from 'lucide-react';
import { ImageUploadDropzone } from '@/components/ui/ImageUploadDropzone';

interface CategoryModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCategoryCreated: (category: any) => void;
}

export function CategoryModal({ isOpen, onClose, onCategoryCreated }: CategoryModalProps) {
  const [name, setName] = useState('');
  const [coverUrl, setCoverUrl] = useState(
    'https://images.unsplash.com/photo-1503951914875-452162b0f3f1?w=800&h=400&fit=crop'
  );
  const [loading, setLoading] = useState(false);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;

    setLoading(true);
    try {
      const res = await fetch('/api/categories', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: name.trim(), coverUrl })
      });
      const data = await res.json();
      if (data.success && data.category) {
        onCategoryCreated(data.category);
        onClose();
      } else {
        alert(data.error || 'Error al crear categoría');
      }
    } catch (err: any) {
      alert('Error de conexión');
    } finally {
      setLoading(false);
    }
  };

  const sampleImages = [
    { label: 'Barbería', url: 'https://images.unsplash.com/photo-1503951914875-452162b0f3f1?w=800&h=400&fit=crop' },
    { label: 'Corte', url: 'https://images.unsplash.com/photo-1599351431202-1e0f0137899a?w=800&h=400&fit=crop' },
    { label: 'Barba', url: 'https://images.unsplash.com/photo-1621605815971-fbc98d665033?w=800&h=400&fit=crop' },
    { label: 'Color', url: 'https://images.unsplash.com/photo-1560066984-138dadb4c035?w=800&h=400&fit=crop' },
    { label: 'Spa & Estética', url: 'https://images.unsplash.com/photo-1570172619644-dfd03ed5d881?w=800&h=400&fit=crop' },
  ];

  return (
    <div className="fixed inset-0 z-60 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-xs transition-opacity">
      <div className="bg-white rounded-3xl shadow-2xl max-w-md w-full overflow-hidden border border-slate-100 flex flex-col">
        {/* Header */}
        <div className="px-6 pt-6 pb-4 flex items-center justify-between border-b border-slate-100">
          <div className="flex items-center gap-2.5">
            <div className="p-2 bg-emerald-50 text-emerald-700 rounded-xl">
              <FolderPlus className="w-5 h-5" />
            </div>
            <div>
              <h3 className="text-base font-bold text-slate-900">Nueva Categoría</h3>
              <p className="text-xs text-slate-500">Agrupá tus servicios de forma visual</p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded-full text-slate-400 hover:text-slate-700 hover:bg-slate-100"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-xs font-bold text-slate-700 mb-1.5">
              Nombre de la categoría
            </label>
            <input
              type="text"
              required
              placeholder="Ej: Colorimetría, Barbería, Spa..."
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs text-slate-900 focus:bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-600 transition-all font-medium"
            />
          </div>

          <div>
            <ImageUploadDropzone
              value={coverUrl}
              onChange={setCoverUrl}
              aspect="compact"
              label="Foto de la categoría"
              helperText="Arrastrá o seleccioná una foto para representar esta categoría"
            />

            <div className="mt-3">
              <span className="block text-[11px] font-bold text-slate-400 mb-1.5 uppercase tracking-wider">
                O elegir imagen predefinida:
              </span>
              <div className="grid grid-cols-5 gap-2">
                {sampleImages.map((img, i) => (
                  <div
                    key={i}
                    onClick={() => setCoverUrl(img.url)}
                    className={`h-11 rounded-xl overflow-hidden border-2 cursor-pointer transition-all ${
                      coverUrl === img.url
                        ? 'border-emerald-600 ring-2 ring-emerald-500/20'
                        : 'border-slate-200 opacity-70 hover:opacity-100'
                    }`}
                    title={img.label}
                  >
                    <img src={img.url} alt={img.label} className="w-full h-full object-cover" />
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="pt-2 flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2.5 text-xs font-semibold text-slate-600 hover:text-slate-900"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={loading || !name.trim()}
              className="px-5 py-2.5 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white text-xs font-bold rounded-xl transition-all shadow-sm"
            >
              {loading ? 'Guardando...' : 'Crear Categoría'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
